import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
// Ola 3: plantilla ÚNICA de impresión de órdenes (compartida con NuevaOrden).
// Cualquier ajuste de diseño de impresión se hace SOLO en printOrden.js.
import { abrirImpresionOrden } from './printOrden';
// ✅ PAGO-VALIDACION-003: criterio ÚNICO de "pago pendiente de validar",
// compartido con la lista de órdenes y el backend. Espejo de
// backend/services/validacionPagos.js — no editar a mano.
import { esPagoVirtual, pagoPendienteValidacion } from './utils/validacionPagos';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ── Ola 3: detección de pantalla móvil (responsive sin CSS externo) ─────────
const useEsMovil = () => {
  const [esMovil, setEsMovil] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setEsMovil(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return esMovil;
};

// Solo etiquetas y colores. El SIGUIENTE paso del flujo lo decide ÚNICAMENTE
// el backend (máquina de estados en orders.js) — llega como orden.siguientePaso.
const ESTADOS = {
  programada:       { label: 'Programada',       color: '#6366f1', bg: '#eef2ff' },
  en_ruta_recogida: { label: 'En Ruta Recogida', color: '#f59e0b', bg: '#fffbeb' },
  en_taller:        { label: 'En Taller',         color: '#8b5cf6', bg: '#f5f3ff' },
  listo_entregar:   { label: 'Listo para Entregar', color: '#0891b2', bg: '#ecfeff' },
  // ✅ FIX ORDEN-CAMBIO-004: etiqueta corregida — el estado es PENDIENTE de facturar
  facturado:        { label: 'Por facturar',       color: '#0284c7', bg: '#e0f2fe' },
  despacho:         { label: 'Despacho',           color: '#d97706', bg: '#fef3c7' },
  en_ruta_entrega:  { label: 'En Ruta Entrega',   color: '#059669', bg: '#ecfdf5' },
  entrega_cobranza: { label: 'Entrega Cobranza',  color: '#dc2626', bg: '#fef2f2' },
  reparacion_proceso: { label: 'Reparación en Proceso', color: '#e11d48', bg: '#ffe4e8' },
  interna_proceso:  { label: 'En Proceso',           color: '#7c3aed', bg: '#f5f3ff' }, // ✅ INTERNA-FLUJO-001
  cuadre_dinero:    { label: 'Completada',         color: '#16a34a', bg: '#f0fdf4' },
  completada:       { label: 'Completada',         color: '#16a34a', bg: '#f0fdf4' },
  cxc:              { label: 'Cuenta por Cobrar',  color: '#b45309', bg: '#fffbeb' },
  anulada:          { label: 'Anulada',           color: '#dc2626', bg: '#fef2f2' },
};

const formatCOP = (v) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);
const formatFecha = (f) => { if (!f) return '—'; try { return new Date(f).toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return '—'; } };
const formatFechaCorta = (f) => { if (!f) return '—'; try { return new Date(f).toLocaleDateString('es-CO'); } catch { return '—'; } };

const FORMAS_DEFAULT = ['Efectivo', 'Transferencia', 'Nequi', 'Daviplata', 'Datafono', 'A crédito (CxC)'];

const DetalleOrden = ({ user, ordenId, onVolver }) => {
  const [orden, setOrden]               = useState(null);
  const [empresa, setEmpresa]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [cambiandoEstado, setCambiando] = useState(false);
  const [notasEstado, setNotasEstado]   = useState('');
const [numeroFactura, setNumeroFactura] = useState('');

  // ── Ola 3: Factura electrónica PDF adjunta a la orden ─────────────────────
  // Sube a Cloudinary (mismo patrón de las fotos) y guarda la referencia en
  // la orden vía backend (que audita la carga y el borrado).
  const [subiendoFactura, setSubiendoFactura] = useState(false);
  const [modalFoto, setModalFoto] = useState(null);

  const adjuntarFacturaPdf = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') return setError('Solo se permite PDF de la factura electrónica.');
    if (file.size > 10 * 1024 * 1024) return setError('El PDF supera 10 MB. Comprime el archivo.');
    setSubiendoFactura(true);
    setError(null);
    try {
      // Firebase Storage via backend (base64) — Cloudinary no servía PDFs.
      const reader = new FileReader();
      const base64 = await new Promise((res, rej) => {
        reader.onload = (e) => res(e.target.result.split(',')[1]);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      await axios.post(`${API}/orders/${ordenId}/factura-pdf`,
        { base64, mimeType: 'application/pdf', nombre: file.name },
        { headers, maxBodyLength: 15 * 1024 * 1024 }
      );
      await cargarOrden();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Error adjuntando la factura');
    }
    setSubiendoFactura(false);
  };

  const eliminarFacturaPdf = async () => {
    if (!window.confirm('¿Eliminar el PDF de la factura adjunta? Quedará registrado en auditoría.')) return;
    try {
      await axios.delete(`${API}/orders/${ordenId}/factura-pdf`, { headers });
      await cargarOrden();
    } catch (e) {
      setError(e.response?.data?.error || 'Error eliminando la factura');
    }
  };
  const [error, setError]               = useState('');
  const [exito, setExito]               = useState('');
  // Pago inline — sin modal separado
  const [mostrarPago, setMostrarPago]   = useState(false);
const [configCerts, setConfigCerts]   = useState([]);
  const [mostrarListaCerts, setMostrarListaCerts] = useState(false);
  const [formaPago, setFormaPago]       = useState('');
  const [montoPago, setMontoPago]       = useState('');
  const [registrandoPago, setRegPago]   = useState(false);
  const [formasPago, setFormasPago]     = useState(FORMAS_DEFAULT);
  // ✅ CAJA-DESTINO-001: caja elegida por quien recibe el pago (EFECTIVO SAS,
  // EFECTIVO SALA DE VENTAS...). Vacía = mapeo automático de configuración.
  const [cajasPago, setCajasPago]       = useState([]);
  const [cajaPagoId, setCajaPagoId]     = useState('');

  // Modal de anular (Ola 1: ahora exige PIN además del motivo)
  const [mostrarAnular, setMostrarAnular] = useState(false);
  const [motivoAnular, setMotivoAnular]   = useState('');
  const [pinAnular, setPinAnular]         = useState('');
  const [verPinAnular, setVerPinAnular]   = useState(false);
  const [anulando, setAnulando]           = useState(false);

  // Ola 2.5: Modal de validar pago electrónico (Admin/Tesorería)
  const [mostrarValidarPago, setMostrarValidarPago] = useState(false);
  const [accionPago, setAccionPago]                 = useState(''); // 'aprobar' | 'rechazar'
  const [motivoValidacion, setMotivoValidacion]     = useState('');
  const [pinValidacion, setPinValidacion]           = useState('');
  const [verPinValidacion, setVerPinValidacion]     = useState(false);
  const [validandoPago, setValidandoPago]           = useState(false);
  // ✅ VALIDAR-CAJA-001: el validador confirma la forma de pago EXACTA
  // (el mensajero solo declaró "electrónico") y elige la caja destino
  // viendo dónde cayó realmente el dinero. Se acaban los traslados.
  const [formaPagoConfirmada, setFormaPagoConfirmada] = useState('');
  const [cajaValidacionId, setCajaValidacionId]       = useState('');
  const [cajasDisp, setCajasDisp]                     = useState([]);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin     = user?.role === 'admin';
  const isTesoreria = user?.role === 'tesoreria';
  const isComercial = user?.role === 'comercial';
  const puedeRegistrarPago  = isAdmin || isTesoreria || isComercial;
  const puedeCargaFactura   = isAdmin || isTesoreria || isComercial;
  const puedeSubirFotos     = isAdmin || isTesoreria || isComercial;
  const puedeAvanzarEstado  = isAdmin;
  const puedeAdjuntarFactura = puedeCargaFactura; // alias — mismo valor
  const esMovil = useEsMovil();

  useEffect(() => { cargarOrden(); cargarFormasPago(); cargarConfigCerts(); cargarCajasPago(); }, [ordenId]);

  // ✅ CAJA-DESTINO-001: cajas activas del tenant para elegir destino del pago
  const cargarCajasPago = async () => {
    try {
      const res = await axios.get(`${API}/cajas`, { headers });
      setCajasPago((res.data || []).filter(c => c.activa !== false));
    } catch { setCajasPago([]); }
  };

  // ✅ TALLER-REPUESTOS-001: el cliente aprueba llamando a quien le conteste —
  // admin, comercial o tesorería pueden registrar la respuesta desde aquí.
  // Al aprobar, el backend agrega los repuestos cotizados a la orden.
  // ✅ FACTURA-FLOW-001: comercial/tesorería registran el N° de factura y la
  // orden avanza sola por la cascada — sin esperar al administrador.
  const [avanzandoFactura, setAvanzandoFactura] = useState(false);
  const registrarFacturaYDespachar = async () => {
    if (!numeroFactura.trim()) return;
    setAvanzandoFactura(true);
    try {
      await axios.put(`${API}/orders/${ordenId}/estado`,
        { avanzar: true, numeroFactura: numeroFactura.trim() }, { headers });
      setNumeroFactura('');
      await cargarOrden();
    } catch (e) {
      alert(e.response?.data?.error || 'Error registrando la factura');
    }
    setAvanzandoFactura(false);
  };

  // ✅ FACTURA-ATASCO-001: guardar el N° de factura SIN mover el estado.
  // Salida para las órdenes que ya avanzaron (entrega_cobranza, cxc,
  // cuadre_dinero...) y el backend les exige la factura para continuar.
  const [guardandoFactura, setGuardandoFactura] = useState(false);
  const guardarNumeroFactura = async () => {
    if (!numeroFactura.trim()) return;
    setGuardandoFactura(true);
    try {
      await axios.post(`${API}/orders/${ordenId}/factura-numero`,
        { numeroFactura: numeroFactura.trim() }, { headers });
      setNumeroFactura('');
      setExito('N° de factura registrado');
      await cargarOrden();
      setTimeout(() => setExito(''), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error registrando la factura');
      setTimeout(() => setError(''), 5000);
    }
    setGuardandoFactura(false);
  };

  // ✅ NO-PAGO-001: reportar que el cliente NO pagó → la orden vuelve a deber.
  // Solo Admin y Tesorería. El cobro original nunca se borra: el backend crea
  // un movimiento de reversión y, si el dinero ya estaba en caja, un ajuste
  // con fecha de hoy. El cuadre ya cerrado no se toca.
  const MOTIVOS_NO_PAGO = [
    { codigo: 'CLIENTE_NO_PAGO',    texto: 'El cliente no pagó' },
    { codigo: 'ERROR_MENSAJERO',    texto: 'El mensajero marcó pagado por error' },
    { codigo: 'CREDITO_AUTORIZADO', texto: 'Quedó a crédito autorizado' },
    { codigo: 'PAGO_NO_LLEGO',      texto: 'Transferencia reportada que nunca llegó' },
    { codigo: 'CHEQUE_DEVUELTO',    texto: 'Cheque devuelto' },
    { codigo: 'OTRO',               texto: 'Otro motivo' }
  ];
  const [mostrarNoPago, setMostrarNoPago] = useState(false);
  const [motivoNoPago, setMotivoNoPago]   = useState('');
  const [notasNoPago, setNotasNoPago]     = useState('');
  const [reversandoCobro, setReversandoCobro] = useState(false);

  const reportarNoPago = async () => {
    if (!motivoNoPago) return setError('Seleccioná el motivo');
    if (motivoNoPago === 'OTRO' && !notasNoPago.trim()) return setError('Escribí la explicación del motivo');
    const montoTexto = formatCOP(orden.montoPagado || 0);
    if (!window.confirm(
      `Se van a reversar ${montoTexto} de la orden ${orden.numeroOrden}.\n\n` +
      `La orden pasa a Cuentas por Cobrar y ese dinero sale del cuadre. ` +
      `El cobro original queda registrado en la auditoría — no se borra nada.\n\n¿Confirmás?`
    )) return;
    setReversandoCobro(true);
    try {
      const res = await axios.post(`${API}/orders/${ordenId}/reversar-cobro`,
        { motivo: motivoNoPago, notas: notasNoPago.trim() }, { headers });
      setMostrarNoPago(false); setMotivoNoPago(''); setNotasNoPago('');
      setExito(res.data?.mensaje || 'Cobro reversado — la orden quedó en cartera');
      await cargarOrden();
      setTimeout(() => setExito(''), 6000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error reversando el cobro');
      setTimeout(() => setError(''), 6000);
    }
    setReversandoCobro(false);
  };

  const responderDefecto = async (defectoIndex, autorizado) => {
    const msg = autorizado
      ? '¿El cliente APROBÓ la reparación? Los repuestos cotizados se agregarán a la orden y se facturarán.'
      : '¿El cliente RECHAZÓ la reparación?';
    if (!window.confirm(msg)) return;
    try {
      await axios.put(`${API}/workshop/ordenes/${ordenId}/defecto/autorizar`,
        { defectoIndex, autorizado }, { headers });
      await cargarOrden();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al responder el defecto');
    }
  };

  const cargarOrden = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/orders/${ordenId}`, { headers });
      setOrden(res.data);
      if (res.data.empresaId) {
        const empRes = await axios.get(`${API}/companies/${res.data.empresaId}`, { headers }).catch(() => null);
        if (empRes) setEmpresa(empRes.data);
      }
    } catch { setError('Error cargando orden'); }
    finally { setLoading(false); }
  };
const cargarConfigCerts = async () => {
    try {
      const res = await axios.get(`${API}/companies/certificados/config`, { headers });
      setConfigCerts(res.data.categorias || []);
    } catch { }
  };
  const cargarFormasPago = async () => {
    try {
      const res = await axios.get(`${API}/configuracion`, { headers });
      const activas = (res.data.formasPago || []).filter(f => f.activa).map(f => f.nombre);
      if (activas.length > 0) setFormasPago(activas);
    } catch { /* usa defaults */ }
  };

 const cambiarEstado = async (nuevoEstado) => {
  try {
    setCambiando(true);
    const res = await axios.put(`${API}/orders/${ordenId}/estado`, {
      // Ola 3: 'avanzar' le dice al backend que calcule el paso legal con la
      // máquina de estados única, leyendo el estado REAL en base de datos.
      // Inmune a pantallas sin refrescar. nuevoEstado viaja solo informativo.
      avanzar: true,
      nuevoEstado,
      notas: notasEstado,
      // Siempre enviamos el N° de factura si está digitado. El backend decide
      // si lo necesita y avanza en cascada automáticamente.
      numeroFactura: numeroFactura || undefined
    }, { headers });
    // El backend pudo encadenar varios estados (cascada automática).
    const estadoReal = res.data?.estado || nuevoEstado;
    setExito(`Estado: ${ESTADOS[estadoReal]?.label || estadoReal}`);
    setNotasEstado('');
    setNumeroFactura('');
      await cargarOrden();
      setTimeout(() => setExito(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error cambiando estado');
    } finally { setCambiando(false); }
  };

  // ── Pago simplificado: un solo clic, sin modal extra ──────────────────────
  const registrarPago = async () => {
    if (!formaPago) return setError('Selecciona la forma de pago');
    try {
      setRegPago(true);
      // ✅ SALDO-UNICO-001 + ABONOS: se cobra sobre el SALDO real.
      // - Efectivo: el campo monto es "paga con" (calculadora de vuelto) →
      //   se registra el saldo completo.
      // - Otras formas: un monto menor al saldo queda como ABONO parcial.
      const saldoReal = Math.max(0, (orden.total || 0) - (orden.montoPagado || 0));
      const montoDigitado = parseFloat(montoPago) || 0;
      const monto = (formaPago === 'Efectivo')
        ? saldoReal
        : (montoDigitado > 0 ? Math.min(montoDigitado, saldoReal) : saldoReal);
      const res = await axios.post(`${API}/orders/${ordenId}/pago`, {
        montoPagado: monto,
        formaPago,
        // Si ya hay N° de factura digitado, lo enviamos: el backend registra
        // el pago Y avanza la orden en cascada automáticamente.
        numeroFactura: numeroFactura || undefined,
        // ✅ CAJA-DESTINO-001: caja elegida por quien recibe (manda sobre el mapeo)
        cajaIdSeleccionada: cajaPagoId || undefined
      }, { headers });

      const caja = res.data.caja;
      if (res.data.abonoParcial) {
        setExito(`🟡 Abono registrado — saldo pendiente ${formatCOP(res.data.saldoRestante || 0)}`);
      } else if (caja?.tipo === 'cxc') {
        setExito('✅ Pago registrado — CxC creada');
      } else if (caja?.tipo === 'duplicado') {
        setExito('ℹ️ Esta orden ya estaba pagada (no se duplicó)');
      } else if (caja?.tipo === 'caja') {
        setExito('✅ Pago registrado — Dinero ingresó a caja automáticamente');
      } else if (caja?.tipo === 'sin_caja') {
        setExito('✅ Pago registrado — ⚠️ Configura el mapeo de cajas en Mi Empresa');
      } else {
        setExito('✅ Pago registrado');
      }

      setMostrarPago(false);
      setFormaPago('');
      setMontoPago('');
      setCajaPagoId(''); // ✅ CAJA-DESTINO-001
      await cargarOrden();
      setTimeout(() => setExito(''), 5000);
    } catch (err) {
      if (err.response?.status === 409) {
        setError(err.response?.data?.error || 'Esta orden ya está pagada o se cobra en el cuadre del mensajero.');
        await cargarOrden();
      } else {
        setError(err.response?.data?.error || 'Error registrando pago');
      }
    } finally { setRegPago(false); }
  };

  // Click en el botón rojo "Anular" → abrir el modal con motivo + PIN
  const abrirAnular = () => {
    setMotivoAnular('');
    setPinAnular('');
    setVerPinAnular(false);
    setError('');
    setMostrarAnular(true);
  };

  const confirmarAnular = async () => {
    setError('');
    if (!motivoAnular.trim()) {
      setError('El motivo de anulación es obligatorio');
      return;
    }
    if (!/^\d{4}$/.test(pinAnular)) {
      setError('El PIN debe ser de 4 dígitos numéricos');
      return;
    }
    try {
      setAnulando(true);
      await axios.put(
        `${API}/orders/${ordenId}/estado`,
        {
          nuevoEstado: 'anulada',
          notas: motivoAnular.trim(),
          pin: pinAnular
        },
        { headers }
      );
      setExito('Orden anulada correctamente');
      setMostrarAnular(false);
      setMotivoAnular('');
      setPinAnular('');
      await cargarOrden();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al anular');
    } finally {
      setAnulando(false);
    }
  };

  // ── Ola 2.5: Validar Pago Electrónico ────────────────────────────────────
  // Cuando el mensajero registra un pago por transferencia/Nequi/datafono,
  // queda pendiente de validación. Admin/Tesorería verifica en el banco y
  // aprueba (suma a caja) o rechaza (la orden pasa a CxC).
  const abrirValidarPago = (accion) => {
    setAccionPago(accion); // 'aprobar' | 'rechazar'
    setMotivoValidacion('');
    setPinValidacion('');
    setVerPinValidacion(false);
    setError('');
    // ✅ VALIDAR-CAJA-001: precargar forma declarada y cajas del tenant.
    // Solo cajas NO efectivo: un pago electrónico cae en banco/billetera.
    setFormaPagoConfirmada(orden?.formaPago || '');
    setCajaValidacionId('');
    if (accion === 'aprobar') {
      axios.get(`${API}/cajas`, { headers })
        .then(r => {
          const noEfectivo = (r.data || []).filter(c =>
            c.activa !== false &&
            !((c.tipo || '').toLowerCase().includes('efectivo') ||
              (c.nombre || '').toLowerCase().includes('efectivo'))
          );
          setCajasDisp(noEfectivo);
          // ✅ CUADRE-CAJA-002: si en el cuadre se eligió un banco destino para
          // este pago, viene sugerido en la orden — se preselecciona aquí.
          const sugerida = orden?.cajaSugeridaId && noEfectivo.some(c => c.id === orden.cajaSugeridaId)
            ? orden.cajaSugeridaId : '';
          if (sugerida) setCajaValidacionId(sugerida);
          else if (noEfectivo.length === 1) setCajaValidacionId(noEfectivo[0].id);
        })
        .catch(() => setCajasDisp([]));
    }
    setMostrarValidarPago(true);
  };

  const confirmarValidarPago = async () => {
    setError('');
    if (accionPago === 'rechazar' && motivoValidacion.trim().length < 5) {
      setError('El motivo de rechazo es obligatorio (mínimo 5 caracteres)');
      return;
    }
    if (!/^\d{4}$/.test(pinValidacion)) {
      setError('El PIN debe ser de 4 dígitos numéricos');
      return;
    }
    try {
      setValidandoPago(true);
      const r = await axios.post(
        `${API}/orders/${ordenId}/validar-pago`,
        {
          aprobado: accionPago === 'aprobar',
          motivo: motivoValidacion.trim(),
          pin: pinValidacion,
          // ✅ VALIDAR-CAJA-001: forma exacta y caja destino confirmadas
          formaPagoConfirmada: accionPago === 'aprobar' ? (formaPagoConfirmada || undefined) : undefined,
          cajaId: accionPago === 'aprobar' ? (cajaValidacionId || undefined) : undefined
        },
        { headers }
      );
      if (r.data.aprobado) {
        setExito('✅ Pago aprobado — Dinero registrado en caja, orden completada');
      } else {
        setExito('🔄 Pago rechazado — Orden movida a CxC');
      }
      setMostrarValidarPago(false);
      setMotivoValidacion('');
      setPinValidacion('');
      await cargarOrden();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al validar el pago');
    } finally {
      setValidandoPago(false);
    }
  };

  const enviarWhatsApp = () => {
    const cel = orden.clienteCelular?.replace(/\D/g, '');
    if (!cel) { setError('El cliente no tiene celular registrado'); return; }
    const msg = `Hola ${orden.clienteNombre}, le informamos sobre su orden ${orden.numeroOrden}.\n\nEstado: ${ESTADOS[orden.estado]?.label}\nTotal: ${formatCOP(orden.total)}\n\nGracias por preferirnos.\n${empresa?.name || ''}`;
    window.open(`https://wa.me/57${cel}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const imprimirOrden = (formato = 'carta') => {
    // Plantilla única compartida (printOrden.js) — misma vista al crear y al ver.
    const ok = abrirImpresionOrden(orden, empresa, formato);
    if (!ok) setError('El navegador bloqueó la ventana de impresión. Permite ventanas emergentes.');
  };

 // ─── IMPRIMIR CERTIFICADO ───────────────────────────────────────────────────
 // Ola 2: ahora llama al endpoint GET /api/orders/:id/certificado/html del
 // backend (fuente única de verdad del HTML). Antes se generaba en frontend
 // con generarHTMLCertificadoDinamico, lo cual duplicaba lógica.
 //
 // Como el endpoint requiere autenticación (header Authorization), no podemos
 // hacer window.open directo a la URL. Estrategia: fetch con Bearer token →
 // obtener HTML → abrir ventana en blanco → escribir el HTML.
 const imprimirCertificado = async (configCat = null) => {
   try {
     const resp = await fetch(`${API}/orders/${ordenId}/certificado/html`, {
       headers: { Authorization: `Bearer ${token}` }
     });
     if (!resp.ok) {
       const errText = await resp.text();
       setError('No se pudo generar el certificado. ' + errText.replace(/<[^>]+>/g, '').slice(0, 200));
       return;
     }
     const html = await resp.text();
     const ventana = window.open('', '_blank');
     if (!ventana) {
       setError('El navegador bloqueó la ventana emergente. Habilítala para imprimir.');
       return;
     }
     ventana.document.write(html);
     ventana.document.close();
     ventana.focus();
     // Recargar datos por si el endpoint marcó certificadoGenerado: true.
     await cargarOrden();
   } catch (e) {
     setError('Error al generar certificado: ' + e.message);
   }
 };

  // Obtener certificados disponibles para esta orden
  const certsDisponibles = () => {
    if (!configCerts.length) return [];
    return configCerts.filter(cat => {
      if (!cat.activo) return false;
      const catNorm = (cat.categoriaProducto || '').toLowerCase();
      return (orden.items || []).some(item => {
        const itemCat = (item.categoria || '').toLowerCase();
        return itemCat.includes(catNorm) || catNorm.includes(itemCat);
      });
    });
  };

  if (loading) return <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>Cargando orden...</div>;
  if (!orden) return <div style={{ padding: '60px', textAlign: 'center', color: '#dc2626' }}>Orden no encontrada</div>;

  const est = ESTADOS[orden.estado] || { label: orden.estado, color: '#666', bg: '#f3f4f6' };

  // Espejo EXACTO de construirFlujo() del backend (orders.js). El frontend NO
  // decide el flujo: solo necesita saber qué etiqueta mostrar en el botón. El
  // backend es la autoridad y puede encadenar en cascada. Mismos valores aquí.
  // ── Ola 3: el frontend YA NO calcula el flujo ──────────────────────────────
  // El GET /orders/:id devuelve orden.siguientePaso calculado por la máquina
  // de estados única del backend. Aquí solo se usa para pintar el botón.
  // (Antes había DOS mapas duplicados en este archivo que divergieron del
  // backend y causaron retrocesos de órdenes — eliminados.)
  const siguienteEstado = orden?.siguientePaso?.siguiente ?? null;

  // Ocultar "Pasar a Cuadre Dinero" si es oficina_rapida y ya está pagado
  // El cuadre es solo para mensajero que cobró efectivo en campo
  // ✅ TEMA-2 FIX: cuando la orden está en 'facturado' y el usuario es
  // comercial/tesorería (no admin), su jugada correcta es la tarjeta
  // "Registrar Factura DIAN" (que SÍ los deja escribir el número y avanzar la
  // orden). El bloque de avance de estado de abajo les mostraba solo el candado
  // "Solo el administrador" y tapaba la acción real — por eso no podían
  // registrar la factura. Aquí ocultamos ese bloque para ellos en ese caso.
  const debeUsarTarjetaFactura = !isAdmin && puedeCargaFactura && orden.estado === 'facturado';

  const mostrarSiguienteEstado = siguienteEstado &&
    orden.estado !== 'anulada' &&
    !debeUsarTarjetaFactura &&
    !(orden.lugarAtencion === 'oficina_rapida' && orden.pagado && siguienteEstado === 'cuadre_dinero');

  const modalFotoRender = modalFoto ? (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 400, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8 }}>
          {modalFoto === 'recogida' ? '📦 Foto de recolección' : modalFoto === 'entrega' ? '✅ Foto de entrega' : '📄 Comprobante de pago'}
        </div>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>Sube la foto que faltó registrar. Quedará en el historial de la orden.</div>
        <label style={{ display: 'block', background: '#6366f1', color: '#fff', borderRadius: 10, padding: '12px 0', textAlign: 'center', fontWeight: 700, cursor: 'pointer', marginBottom: 10 }}>
          📷 Seleccionar archivo
          <input type="file" accept="image/*" hidden onChange={async (e) => {
            const file = e.target.files[0]; if (!file) return;
            try {
              const fd = new FormData();
              fd.append('file', file); fd.append('upload_preset', 'control360'); fd.append('folder', 'control360/logistica');
              const up = await fetch('https://api.cloudinary.com/v1_1/dk8hposft/image/upload', { method: 'POST', body: fd });
              const upJson = await up.json();
              if (!upJson.secure_url) throw new Error('Error subiendo');
              await axios.post(`${API}/orders/${ordenId}/fotos-adicionales`, { tipo: modalFoto, url: upJson.secure_url }, { headers });
              await cargarOrden(); setModalFoto(null);
            } catch (err) { alert(err.message || 'Error subiendo foto'); }
            e.target.value = '';
          }} />
        </label>
        <button onClick={() => setModalFoto(null)} style={{ width: '100%', background: '#f3f4f6', border: 'none', borderRadius: 10, padding: '11px 0', fontWeight: 700, cursor: 'pointer' }}>Cancelar</button>
      </div>
    </div>
  ) : null;

  return (
    <>
    {modalFotoRender}
    <div style={{ ...s.wrapper, ...(esMovil ? { padding: '14px 10px' } : {}) }}>
      {/* HEADER */}
      <div style={{ ...s.pageHeader, ...(esMovil ? { flexDirection: 'column', alignItems: 'stretch' } : {}) }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', ...(esMovil ? { flexWrap: 'wrap', gap: '10px' } : {}) }}>
          <button onClick={onVolver} style={s.btnBack}>← Volver</button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <h2 style={s.pageTitle}>{orden.numeroOrden}</h2>
              <span style={{ ...s.estadoBadge, background: est.bg, color: est.color }}>{est.label}</span>
              {orden.prioridad === 'urgente' && <span style={s.urgBadge}>🔴 URGENTE</span>}
              {orden.prioridad === 'alta' && <span style={{ ...s.urgBadge, background: '#fffbeb', color: '#d97706' }}>🟡 ALTA</span>}
              {orden.pagado && <span style={{ ...s.estadoBadge, background: '#f0fdf4', color: '#16a34a' }}>✅ PAGADO</span>}
              {orden.requiereFactura && <span style={{ ...s.estadoBadge, background: '#fef3c7', color: '#d97706' }}>📄 FACTURA DIAN</span>}
            </div>
            <p style={s.pageSubtitle}>{orden.clienteNombre} {orden.sucursalNombre && `— ${orden.sucursalNombre}`}</p>
          </div>
        </div>

        <div style={{ ...s.acciones, ...(esMovil ? { width: '100%' } : {}) }}>
          {isAdmin && orden.estado !== 'cuadre_dinero' && orden.estado !== 'anulada' && (
            <button onClick={abrirAnular} style={{ ...s.btnImprimir, background: '#dc2626' }}>🚫 Anular</button>
          )}
          <button onClick={enviarWhatsApp} style={s.btnWa}>📱 WhatsApp</button>
          <button onClick={() => imprimirOrden('carta')} style={s.btnImprimir}>🖨️ Imprimir</button>
          <button onClick={() => imprimirOrden('pos')} style={s.btnImprimir}>🧾 POS</button>
          {(orden.generaCertificado || certsDisponibles().length > 0) && (
            <div style={{ position: 'relative' }}>
              <button onClick={() => setMostrarListaCerts(!mostrarListaCerts)} style={s.btnCert}>
                📜 Certificado {certsDisponibles().length > 1 ? `(${certsDisponibles().length})` : ''}
              </button>
              {mostrarListaCerts && (
                <div style={{ position: 'absolute', top: '100%', right: 0, background: '#fff', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb', zIndex: 100, minWidth: 220, padding: 8 }}>
                  {certsDisponibles().length === 0 ? (
                    <div onClick={() => { imprimirCertificado(); setMostrarListaCerts(false); }}
                      style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, borderRadius: 7, display: 'flex', alignItems: 'center', gap: 8 }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      📜 Certificado de Mantenimiento
                    </div>
                  ) : (
                    certsDisponibles().map(cat => (
                      <div key={cat.id}
                        onClick={() => { imprimirCertificado(cat); setMostrarListaCerts(false); }}
                        style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, borderRadius: 7, display: 'flex', alignItems: 'center', gap: 8 }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        📄 {cat.nombreDocumento}
                        <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 'auto' }}>{cat.norma}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {error && <div style={s.alertError}>{error} <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}>✕</button></div>}
      {exito && <div style={s.alertExito}>{exito}</div>}

      {/* ── Ola 2.5: BANNER Pago Electrónico Pendiente de Validación ────────── */}
      {(() => {
        // ✅ PAGO-VALIDACION-003 (corrige PAGO-ADMIN-002): el predicado es el
        // MISMO de la lista de órdenes y del dashboard. Antes este banner
        // agregaba una condición que la lista no tenía (!dineroEnCaja): la
        // orden aparecía marcada "⏳ Validar pago" en la lista y acá no salía
        // el botón — quedaba pendiente para siempre y no había cómo validarla
        // (caso real OS-0528 y OS-0516, agosto 2026).
        //
        // El dinero en caja ya no oculta el banner: entra a caja por varias
        // vías que nadie revisó contra el banco. Lo que evita pedirle al dueño
        // que valide su propio pago es que ahora ese pago NACE validado en el
        // backend cuando lo registra admin o tesorería.
        const tienePagoElectronico = esPagoVirtual(orden.formaPago);
        const dineroYaEnCaja = orden.dineroEnCaja === true;
        const pendienteValidar = pagoPendienteValidacion(orden);
        const puedeValidar = isAdmin || (user?.role === 'tesoreria');

        if (!pendienteValidar) {
          // Si ya fue validado, mostrar nota informativa
          if (tienePagoElectronico && orden.pagoValidado === true) {
            return (
              <div style={{
                background: '#f0fdf4', border: '1px solid #86efac',
                color: '#166534', padding: '10px 16px', borderRadius: 10,
                marginBottom: 16, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10
              }}>
                <span style={{ fontSize: 18 }}>✅</span>
                <div style={{ flex: 1 }}>
                  <strong>Pago electrónico validado</strong>
                  {orden.pagoValidadoPorNombre && (
                    <span style={{ color: '#15803d', marginLeft: 8 }}>
                      por {orden.pagoValidadoPorNombre}
                      {orden.pagoValidadoEn && ` — ${new Date(orden.pagoValidadoEn).toLocaleString('es-CO', { timeZone: 'America/Bogota' })}`}
                    </span>
                  )}
                </div>
              </div>
            );
          }
          if (tienePagoElectronico && orden.pagoRechazado) {
            return (
              <div style={{
                background: '#fef2f2', border: '1px solid #fca5a5',
                color: '#991b1b', padding: '12px 16px', borderRadius: 10,
                marginBottom: 16, fontSize: 13
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <span style={{ fontSize: 18 }}>❌</span>
                  <strong>Pago electrónico rechazado</strong>
                </div>
                {orden.pagoValidacionMotivo && (
                  <div style={{ paddingLeft: 28, color: '#7f1d1d', fontStyle: 'italic' }}>
                    Motivo: {orden.pagoValidacionMotivo}
                  </div>
                )}
                <div style={{ paddingLeft: 28, color: '#991b1b', marginTop: 4, fontSize: 12 }}>
                  La orden pasó a CxC. El cliente debe este dinero.
                </div>
              </div>
            );
          }
          return null;
        }

        // Banner activo: pendiente de validación
        return (
          <div style={{
            background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
            border: '2px solid #f59e0b',
            borderRadius: 12, padding: '16px 20px',
            marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16,
            boxShadow: '0 4px 12px rgba(245, 158, 11, 0.15)'
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%', background: '#fef3c7',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 24, flexShrink: 0, border: '2px solid #f59e0b'
            }}>
              ⏳
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#92400e', marginBottom: 2 }}>
                Pago electrónico pendiente de validación
              </div>
              <div style={{ fontSize: 13, color: '#78350f' }}>
                Se registró un pago por <strong>{orden.formaPago}</strong> de <strong>{formatCOP(orden.total)}</strong>.
                {puedeValidar
                  ? ' Verifica el ingreso en el banco antes de aprobar.'
                  : ' Admin o Tesorería deben verificarlo en el banco.'
                }
                {dineroYaEnCaja && (
                  <div style={{ marginTop: 4, fontSize: 12, color: '#92400e' }}>
                    El dinero ya figura en caja. Aprobar solo deja la constancia de que llegó al banco:
                    el candado por monto impide que se vuelva a sumar.
                  </div>
                )}
              </div>
            </div>
            {puedeValidar && (
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button
                  onClick={() => abrirValidarPago('rechazar')}
                  style={{
                    padding: '10px 18px', background: '#fff',
                    color: '#dc2626', border: '2px solid #dc2626',
                    borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13
                  }}>
                  ❌ Rechazar
                </button>
                <button
                  onClick={() => abrirValidarPago('aprobar')}
                  style={{
                    padding: '10px 18px', background: '#16a34a',
                    color: '#fff', border: 'none',
                    borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13
                  }}>
                  ✅ Aprobar
                </button>
              </div>
            )}
          </div>
        );
      })()}

      <div style={{ ...s.grid2, ...(esMovil ? { gridTemplateColumns: '1fr' } : {}) }}>
        {/* COLUMNA IZQUIERDA — empieza con Cliente */}
        <div>
          {/* Cliente info — PRIMERO para verlo rápido */}
          <div style={s.card}>
            <h3 style={s.cardTitulo}>👥 Cliente</h3>
            <div style={{ ...s.infoGrid, ...(esMovil ? { gridTemplateColumns: '1fr' } : {}) }}>
              <div style={s.infoItem}><span style={s.infoLabel}>Nombre</span><strong>{orden.clienteNombre}</strong></div>
              {orden.clienteNit && <div style={s.infoItem}><span style={s.infoLabel}>NIT/CC</span><span>{orden.clienteNit}</span></div>}
              {orden.clienteCelular && (
                <div style={s.infoItem}>
                  <span style={s.infoLabel}>Celular</span>
                  <a href={`tel:${orden.clienteCelular}`} style={{ color: '#7c3aed', fontWeight: 600 }}>{orden.clienteCelular}</a>
                </div>
              )}
              {(orden.clienteDireccionPrincipal || orden.clienteDireccion) && (
                <div style={s.infoItem}>
                  <span style={s.infoLabel}>Dirección</span>
                  <a href={`https://maps.google.com/?q=${encodeURIComponent(orden.clienteDireccionPrincipal || orden.clienteDireccion)}`} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: 13 }}>
                    📍 {orden.clienteDireccionPrincipal || orden.clienteDireccion}
                  </a>
                </div>
              )}
              {orden.sucursalDireccion && orden.sucursalDireccion !== (orden.clienteDireccionPrincipal || orden.clienteDireccion) && (
                <div style={s.infoItem}>
                  <span style={s.infoLabel}>Dir. sucursal</span>
                  <a href={`https://maps.google.com/?q=${encodeURIComponent(orden.sucursalDireccion)}`} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: 13 }}>
                    📍 {orden.sucursalDireccion}
                  </a>
                </div>
              )}
              {orden.ciudad && <div style={s.infoItem}><span style={s.infoLabel}>Ciudad</span><span>{orden.ciudad}</span></div>}
              {orden.emailLegal && (
                <div style={s.infoItem}>
                  <span style={s.infoLabel}>Email</span>
                  <a href={`mailto:${orden.emailLegal}`} style={{ color: '#7c3aed', fontSize: 13 }}>{orden.emailLegal}</a>
                </div>
              )}
            </div>
          </div>

          {/* Info básica de la orden — SEGUNDO */}
          <div style={s.card}>
            <h3 style={s.cardTitulo}>📋 Información</h3>
            <div style={{ ...s.infoGrid, ...(esMovil ? { gridTemplateColumns: '1fr' } : {}) }}>
              <div style={s.infoItem}><span style={s.infoLabel}>Empresa</span><strong style={{ color: '#7c3aed' }}>{orden.empresaNombre}</strong></div>
              <div style={s.infoItem}><span style={s.infoLabel}>Tipo</span><span>{orden.tipoOrden}</span></div>
              <div style={s.infoItem}><span style={s.infoLabel}>Atención</span><span>{orden.lugarAtencion || '—'}</span></div>
              <div style={s.infoItem}><span style={s.infoLabel}>Fecha programada</span><span>{formatFechaCorta(orden.fechaProgramada)}{orden.horaProgramada && ` — ${orden.horaProgramada}`}</span></div>
              <div style={s.infoItem}><span style={s.infoLabel}>Mensajero</span><span>{orden.mensajeroNombre || <em style={{ color: '#9ca3af' }}>Sin asignar</em>}</span></div>
              <div style={s.infoItem}><span style={s.infoLabel}>Creado por</span><span>{orden.creadoPorNombre}</span></div>
{orden.numeroFactura && <div style={s.infoItem}><span style={s.infoLabel}>📄 N° Factura</span><strong style={{ color: '#0284c7' }}>{orden.numeroFactura}</strong></div>}
              <div style={s.infoItem}>
                <span style={s.infoLabel}>📎 Factura electrónica (PDF)</span>
                {orden.facturaPdfUrl ? (
                  <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <a href={orden.facturaPdfUrl} target="_blank" rel="noopener noreferrer"
                      style={{ color: '#0284c7', fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>
                      {/* ✅ FACTURA-FLOW-001: nombre del archivo visible — el N° DIAN se lee sin descargar */}
                      ⬇ {orden.facturaPdfNombre || 'Ver / Descargar PDF'}
                    </a>
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>{orden.facturaPdfSubidaPor ? `subida por ${orden.facturaPdfSubidaPor}` : ''}</span>
                    {puedeAdjuntarFactura && (
                      <button onClick={eliminarFacturaPdf} title="Eliminar (queda en auditoría)"
                        style={{ border: 'none', background: '#fef2f2', color: '#b91c1c', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>🗑 Quitar</button>
                    )}
                  </span>
                ) : puedeAdjuntarFactura ? (
                  <label style={{ display: 'inline-block', background: subiendoFactura ? '#9ca3af' : '#eff6ff', color: subiendoFactura ? '#fff' : '#1d4ed8', border: '1px dashed #93c5fd', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: subiendoFactura ? 'wait' : 'pointer' }}>
                    {subiendoFactura ? '⏳ Subiendo PDF...' : '📎 Adjuntar PDF'}
                    <input type="file" accept="application/pdf" hidden disabled={subiendoFactura}
                      onChange={e => { adjuntarFacturaPdf(e.target.files[0]); e.target.value = ''; }} />
                  </label>
                ) : (
                  <span style={{ fontSize: 12, color: '#9ca3af' }}>Sin adjuntar</span>
                )}
              </div>
              {orden.notasOrden && <div style={{ ...s.infoItem, gridColumn: '1/-1' }}><span style={s.infoLabel}>📝 Notas</span><span style={{ color: '#374151', fontStyle: 'italic' }}>{orden.notasOrden}</span></div>}
            </div>
          </div>

          {/* Productos */}
          <div style={s.card}>
            <h3 style={s.cardTitulo}>📦 Productos / Servicios</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Producto', 'Cant.', 'Precio', 'Desc.', 'Notas', 'Total'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(orden.items || []).map((item, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontWeight: 600 }}>{item.nombre}</div>
                      <div style={{ fontSize: '11px', color: '#9ca3af' }}>{item.categoria}</div>
                    </td>
                    <td style={{ padding: '10px 12px' }}>{item.cantidad}</td>
                    <td style={{ padding: '10px 12px' }}>{formatCOP(item.precioUnitario)}</td>
                    <td style={{ padding: '10px 12px', color: item.descuento > 0 ? '#dc2626' : '#9ca3af' }}>{item.descuento > 0 ? `-${item.descuento}%` : '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {item.notas ? <span style={{ background: '#ede9fe', color: '#7c3aed', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>{item.notas}</span> : <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: '#16a34a' }}>{formatCOP(item.subtotalItem)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totales */}
            <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '0 0 8px 8px' }}>
              <div style={s.totalRow}><span style={{ color: '#6b7280' }}>Subtotal</span><span>{formatCOP(orden.subtotal)}</span></div>
              {orden.ivaPct > 0 && <div style={s.totalRow}><span style={{ color: '#6b7280' }}>IVA ({orden.ivaPct}%)</span><span>{formatCOP(orden.ivaValor)}</span></div>}
              <div style={{ ...s.totalRow, fontWeight: 800, fontSize: '16px', borderTop: '2px solid #e5e7eb', paddingTop: '8px', marginTop: '4px' }}>
                <span>TOTAL</span><strong style={{ color: '#16a34a' }}>{formatCOP(orden.total)}</strong>
              </div>
              {/* ✅ SALDO-UNICO-001: el banner refleja el SALDO real, no la
                  bandera. Pagada solo si saldo 0; si hay abonos muestra
                  cuánto va pagado y cuánto se debe. */}
              {(() => {
                const saldoReal = Math.max(0, (orden.total || 0) - (orden.montoPagado || 0));
                if ((orden.montoPagado || 0) <= 0) return null;
                if (saldoReal <= 1) return (
                  <div style={{ marginTop: '8px', padding: '8px 12px', background: '#f0fdf4', borderRadius: '6px', fontSize: '13px', color: '#16a34a', display: 'flex', justifyContent: 'space-between' }}>
                    <span>✅ Pagado — {orden.formaPago}</span>
                    <strong>{formatCOP(orden.montoPagado)}</strong>
                  </div>
                );
                return (
                  <div style={{ marginTop: '8px', padding: '8px 12px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '6px', fontSize: '13px', color: '#92400e' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>🟡 Abonado — {orden.formaPago}</span>
                      <strong>{formatCOP(orden.montoPagado)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2, fontWeight: 800 }}>
                      <span>Saldo pendiente</span>
                      <span style={{ color: '#dc2626' }}>{formatCOP(saldoReal)}</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* ✅ FACTURA-ATASCO-001: orden que YA avanzó y le falta la factura.
              Sin esta tarjeta no había dónde digitar el número y la orden
              quedaba bloqueada para siempre. Guarda sin mover el estado. */}
          {puedeCargaFactura && orden.requiereFactura && !orden.numeroFactura
            && !['facturado', 'anulada'].includes(orden.estado) && (
            <div style={{ ...s.card, border: '2px solid #dc2626', background: '#fff7f7' }}>
              <h3 style={{ ...s.cardTitulo, color: '#dc2626' }}>⚠️ Falta el N° de factura DIAN</h3>
              <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 10px' }}>
                Esta orden avanzó a <strong>{ESTADOS[orden.estado]?.label || orden.estado}</strong> sin número de
                factura y el sistema no la deja continuar sin él. Regístralo aquí — no cambia el estado de la orden.
              </p>
              <input style={{ ...s.input, marginBottom: 10 }} placeholder="FE-0001..."
                value={numeroFactura} onChange={e => setNumeroFactura(e.target.value)} />
              <button onClick={guardarNumeroFactura}
                disabled={!numeroFactura.trim() || guardandoFactura}
                style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none',
                  background: (!numeroFactura.trim() || guardandoFactura) ? '#9ca3af' : '#dc2626',
                  color: '#fff', fontWeight: 800, fontSize: 13,
                  cursor: (!numeroFactura.trim() || guardandoFactura) ? 'not-allowed' : 'pointer' }}>
                {guardandoFactura ? '⏳ Guardando...' : '📄 Guardar N° de factura'}
              </button>
            </div>
          )}

          {/* ✅ FACTURA-FLOW-001: registrar factura DIAN — comercial/tesorería */}
          {!isAdmin && puedeCargaFactura && orden.estado === 'facturado' && (
            <div style={{ ...s.card, border: '2px solid #7c3aed' }}>
              <h3 style={s.cardTitulo}>📄 Registrar Factura DIAN</h3>
              <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 10px' }}>
                Al registrar el número, la orden avanza automáticamente al siguiente paso del flujo — sin esperar al administrador.
              </p>
              {orden.facturaPdfNombre && (
                <p style={{ fontSize: 12, color: '#0284c7', margin: '0 0 10px' }}>
                  📎 Archivo adjunto: <strong>{orden.facturaPdfNombre}</strong>
                </p>
              )}
              <input style={{ ...s.input, marginBottom: 10 }} placeholder="FE-0001..."
                value={numeroFactura} onChange={e => setNumeroFactura(e.target.value)} />
              <button onClick={registrarFacturaYDespachar}
                disabled={!numeroFactura.trim() || avanzandoFactura}
                style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none',
                  background: (!numeroFactura.trim() || avanzandoFactura) ? '#9ca3af' : '#7c3aed',
                  color: '#fff', fontWeight: 800, fontSize: 13,
                  cursor: (!numeroFactura.trim() || avanzandoFactura) ? 'not-allowed' : 'pointer' }}>
                {avanzandoFactura ? '⏳ Registrando...' : '📄 Registrar factura y avanzar'}
              </button>
            </div>
          )}

          {/* ✅ TALLER-REPUESTOS-001: Defectos de taller y autorización del cliente */}
          {(orden.tallerDefectos || []).length > 0 && (
            <div style={s.card}>
              <h3 style={s.cardTitulo}>🔧 Defectos de Taller</h3>
              {(orden.tallerDefectos || []).map((d, di) => (
                <div key={di} style={{
                  border: `1px solid ${d.estado === 'pendiente_autorizacion' ? '#fca5a5' : '#e5e7eb'}`,
                  background: d.estado === 'pendiente_autorizacion' ? '#fff1f2' : d.estado === 'autorizado' ? '#f0fdf4' : '#f9fafb',
                  borderRadius: 8, padding: '10px 14px', marginBottom: 8
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ fontSize: 13 }}>
                      <strong>{d.descripcion}</strong>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>Registrado por {d.tecnicoNombre || '—'}</div>
                      {(d.repuestos || []).length > 0 && (
                        <div style={{ color: '#6b7280', marginTop: 4 }}>
                          {(d.repuestos || []).map((r, ri) => (
                            <div key={ri}>• {r.cantidad} x {r.nombre} — {formatCOP((Number(r.precioUnitario) || 0) * (Number(r.cantidad) || 1))}</div>
                          ))}
                        </div>
                      )}
                      <div style={{ marginTop: 4, fontWeight: 700 }}>Total cotizado: {formatCOP(d.costoReparacion || 0)}</div>
                      {d.estado !== 'pendiente_autorizacion' && (
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                          {d.estado === 'autorizado' ? '✅ Autorizado' : '❌ Rechazado'} por {d.respondidoPorNombre || '—'} {d.fechaRespuesta ? `— ${formatFechaCorta(d.fechaRespuesta)}` : ''}
                        </div>
                      )}
                    </div>
                    {d.estado === 'pendiente_autorizacion' && (isAdmin || isComercial || isTesoreria) && (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                        <button onClick={() => responderDefecto(di, true)} style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>✅ Cliente aprobó</button>
                        <button onClick={() => responderDefecto(di, false)} style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: '#6b7280', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>❌ Rechazó</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Certificado */}
          {orden.generaCertificado && (
            <div style={s.card}>
              <h3 style={s.cardTitulo}>📜 Certificado de Mantenimiento</h3>
             {orden.certificadoGenerado ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: '#16a34a', fontWeight: 700 }}>✅ Certificado generado</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>Generado: {formatFechaCorta(orden.certificadoFecha)}</div>
                    {orden.alertaRenovacion && <div style={{ fontSize: '12px', color: '#f59e0b', marginTop: '2px' }}>⏰ Alerta renovación: {formatFechaCorta(orden.alertaRenovacion)}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {/* Botón imprimir con lista dinámica */}
                    <div style={{ position: 'relative' }}>
                      <button onClick={() => setMostrarListaCerts(v => !v)} style={s.btnCert}>
                        🖨️ Imprimir {certsDisponibles().length > 1 ? `(${certsDisponibles().length})` : ''}
                      </button>
                      {mostrarListaCerts && (
                        <div style={{ position: 'absolute', top: '100%', right: 0, background: '#fff', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb', zIndex: 100, minWidth: 220, padding: 8 }}>
                          {certsDisponibles().length === 0 ? (
                            <div onClick={() => { imprimirCertificado(); setMostrarListaCerts(false); }}
                              style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, borderRadius: 7 }}
                              onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                              📜 Certificado de Mantenimiento
                            </div>
                          ) : (
                            certsDisponibles().map(cat => (
                              <div key={cat.id}
                                onClick={() => { imprimirCertificado(cat); setMostrarListaCerts(false); }}
                                style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, borderRadius: 7, display: 'flex', alignItems: 'center', gap: 8 }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                📄 {cat.nombreDocumento}
                                <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 'auto' }}>{cat.norma}</span>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    <button onClick={() => {
                      const cel = orden.clienteCelular?.replace(/\D/g, '');
                      const msg = `Hola ${orden.clienteNombre}, adjuntamos el certificado correspondiente a la orden ${orden.numeroOrden}.`;
                      window.open(`https://wa.me/57${cel}?text=${encodeURIComponent(msg)}`, '_blank');
                    }} style={s.btnWa}>📱 Enviar</button>
                  </div>
                </div>
              ) : (
                <div style={{ color: '#9ca3af', fontSize: '14px' }}>
                  ⏳ El certificado se generará automáticamente al completar el cuadre de dinero
                </div>
              )}
            </div>
          )}

          {puedeSubirFotos && (() => {
            const btns = [];
            if (!orden.fotoRecogida && ['en_taller','facturado','despacho','en_ruta_entrega','entrega_cobranza','completada'].includes(orden.estado))
              btns.push(['recogida','📦 Subir foto recolección','#fffbeb','#fcd34d','#92400e']);
            if (!orden.fotoEntrega && ['entrega_cobranza','cuadre_dinero','completada','cxc'].includes(orden.estado))
              btns.push(['entrega','✅ Subir foto entrega','#f0fdf4','#86efac','#166534']);
            if (!orden.fotoTransferenciaUrl && !orden.fotoTransferencia && orden.pagado && orden.formaPago && !['Efectivo','A crédito (CxC)','A crédito'].includes(orden.formaPago))
              btns.push(['comprobante','📄 Subir comprobante','#eff6ff','#93c5fd','#1d4ed8']);
            if (!btns.length) return null;
            return <div style={{ marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {btns.map(([tipo,label,bg,border,color]) => (
                <button key={tipo} onClick={() => setModalFoto(tipo)}
                  style={{ fontSize: 11, padding: '5px 10px', background: bg, border: `1px solid ${border}`, borderRadius: 7, cursor: 'pointer', color, fontWeight: 700 }}>
                  {label}
                </button>
              ))}
            </div>;
          })()}

          {/* GALERÍA DE FOTOS */}
          {(() => {
            const todasFotos = [
  ...(orden.fotoRecogida ? [{ url: orden.fotoRecogida, tipo: '📦 Recogida' }] : []),
  ...(orden.tallerPasos || []).filter(p => p.foto).map(p => ({ url: p.foto, tipo: `🔧 Taller — ${p.pasoNombre || ''}` })),
  ...(orden.tallerDefectos || []).filter(d => d.foto).map(d => ({ url: d.foto, tipo: `⚠️ Defecto — ${d.descripcion?.substring(0, 30) || ''}` })),
  ...(orden.fotoEntrega ? [{ url: orden.fotoEntrega, tipo: '✅ Entrega' }] : []),
  // FIX Bug 1: aceptar tanto fotoTransferenciaUrl (cuando carga comercial al crear)
  // como fotoTransferencia (cuando carga mensajero al cobrar). Antes solo veía la 2da.
  ...((orden.fotoTransferenciaUrl || orden.fotoTransferencia)
    ? [{ url: orden.fotoTransferenciaUrl || orden.fotoTransferencia, tipo: '📄 Comprobante pago' }]
    : []),
];
            if (todasFotos.length === 0) return null;
            return (
              <div style={s.card}>
                <h3 style={s.cardTitulo}>📷 Galería de Fotos ({todasFotos.length})</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
                  {todasFotos.map((foto, i) => (
                    <div key={i} style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb', cursor: 'pointer' }}
                      onClick={() => window.open(foto.url, '_blank')}>
                      <img src={foto.url} alt={foto.tipo}
                        style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }}
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                      <div style={{ padding: '4px 8px', fontSize: 11, color: '#6b7280', background: '#f9fafb', fontWeight: 600 }}>
                        {foto.tipo}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>

        {/* COLUMNA DERECHA */}
        <div>
          {/* ✅ NO-PAGO-001: el mensajero puede marcar "cobrado" una orden que el
              cliente nunca pagó (caso real OS-0654 Sócrates). Sin esta salida,
              la plata quedaba en el cuadre del mensajero y la deuda no llegaba
              nunca a cartera. Solo Admin/Tesorería. */}
          {(isAdmin || isTesoreria) && orden.estado !== 'anulada'
            && ((orden.montoPagado || 0) > 0 || orden.cobroRevertido) && (
            <div style={{ ...s.card, border: '1px solid #fcd34d', background: '#fffdf5' }}>
              <h3 style={{ ...s.cardTitulo, color: '#b45309' }}>🚩 ¿El cliente no pagó?</h3>

              {orden.cobroRevertido && (
                <div style={{ padding: '8px 12px', background: '#fff1f2', border: '1px solid #fecaca',
                  borderRadius: 8, fontSize: 12, color: '#b91c1c', marginBottom: 10 }}>
                  Cobro reversado por <strong>{orden.cobroRevertidoPor}</strong> — {orden.cobroRevertidoMotivo}
                  {orden.cobroRevertidoEn && <> · {formatFechaCorta(orden.cobroRevertidoEn)}</>}
                </div>
              )}

              {(orden.montoPagado || 0) > 0 && (!mostrarNoPago ? (
                <>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                    Esta orden tiene <strong>{formatCOP(orden.montoPagado)}</strong> registrados como cobrados
                    {orden.mensajeroNombre ? <> por <strong>{orden.mensajeroNombre}</strong></> : null}.
                    Si ese pago nunca entró, reportalo: la orden pasa a Cuentas por Cobrar y el dinero sale del cuadre.
                  </div>
                  <button onClick={() => setMostrarNoPago(true)}
                    style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #f59e0b',
                      background: '#fff', color: '#b45309', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
                    🚩 Reportar no pago
                  </button>
                </>
              ) : (
                <>
                  <label style={{ ...s.label, marginBottom: 6, display: 'block' }}>Motivo <span style={{ color: '#dc2626' }}>*</span></label>
                  <select style={{ ...s.input, marginBottom: 10 }} value={motivoNoPago}
                    onChange={e => setMotivoNoPago(e.target.value)}>
                    <option value="">Seleccioná el motivo...</option>
                    {MOTIVOS_NO_PAGO.map(m => <option key={m.codigo} value={m.codigo}>{m.texto}</option>)}
                  </select>
                  <textarea style={{ ...s.input, marginBottom: 10, minHeight: 64, resize: 'vertical' }}
                    placeholder={motivoNoPago === 'OTRO' ? 'Explicá qué pasó (obligatorio)...' : 'Detalle (opcional)...'}
                    value={notasNoPago} onChange={e => setNotasNoPago(e.target.value)} />
                  <div style={{ padding: '8px 12px', background: '#fffbeb', border: '1px solid #fcd34d',
                    borderRadius: 8, fontSize: 11.5, color: '#92400e', marginBottom: 10, lineHeight: 1.5 }}>
                    Al confirmar: la orden queda debiendo <strong>{formatCOP(orden.total)}</strong> en
                    Cuentas por Cobrar, el dinero sale del cuadre y —si ya había entrado a caja— se registra un
                    ajuste <strong>con fecha de hoy</strong>. Los cierres de caja anteriores no se tocan.
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={reportarNoPago} disabled={reversandoCobro || !motivoNoPago}
                      style={{ ...s.btnPrimario, background: reversandoCobro || !motivoNoPago ? '#9ca3af' : '#dc2626' }}>
                      {reversandoCobro ? 'Reversando...' : '🚩 Confirmar no pago'}
                    </button>
                    <button onClick={() => { setMostrarNoPago(false); setMotivoNoPago(''); setNotasNoPago(''); }}
                      style={s.btnSecundario}>Cancelar</button>
                  </div>
                </>
              ))}
            </div>
          )}

          {/* ── PAGO — inline, sin modal ──────────────────────────────────── */}
          {!orden.pagado && orden.estado !== 'anulada' && (
            <div style={s.card}>
              <h3 style={s.cardTitulo}>💰 Pago</h3>
              {!mostrarPago ? (
                <div>
                  <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
                    {/* ✅ SALDO-UNICO-001: se cobra el saldo, no el total a ciegas */}
                    {(orden.montoPagado || 0) > 0
                      ? <>Saldo a cobrar: <strong style={{ color: '#dc2626', fontSize: 16 }}>{formatCOP(Math.max(0, (orden.total || 0) - (orden.montoPagado || 0)))}</strong> <span style={{ color: '#9ca3af' }}>(abonado {formatCOP(orden.montoPagado)})</span></>
                      : <>Total a cobrar: <strong style={{ color: '#111', fontSize: 16 }}>{formatCOP(orden.total)}</strong></>}
                  </div>
                  {puedeRegistrarPago ? (
                    <button onClick={() => setMostrarPago(true)} style={s.btnPrimario}>
                      + Registrar pago
                    </button>
                  ) : (
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>El cobro lo procesa admin o tesorería</div>
                  )}
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
                    Saldo: <strong style={{ color: '#16a34a', fontSize: 16 }}>{formatCOP(Math.max(0, (orden.total || 0) - (orden.montoPagado || 0)))}</strong>
                  </div>

                  {/* Formas de pago como botones visuales */}
                  <div style={s.campo}>
                    <label style={s.label}>Forma de pago *</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {formasPago.map(f => (
                        <button key={f} onClick={() => setFormaPago(f)} style={{
                          padding: '8px 14px', borderRadius: 8, border: '2px solid',
                          borderColor: formaPago === f ? '#667eea' : '#e5e7eb',
                          background: formaPago === f ? '#eef2ff' : '#f9fafb',
                          color: formaPago === f ? '#4f46e5' : '#374151',
                          fontWeight: formaPago === f ? 700 : 500,
                          fontSize: 13, cursor: 'pointer'
                        }}>{f}</button>
                      ))}
                    </div>
                  </div>

                  {/* ✅ CAJA-DESTINO-001: caja donde ENTRA el dinero. Si hay
                      más de una caja, quien recibe elige (EFECTIVO SAS,
                      EFECTIVO SALA DE VENTAS...). Vacío = mapeo automático. */}
                  {cajasPago.length > 1 && (
                    <div style={s.campo}>
                      <label style={s.label}>🏦 Caja donde entra el dinero</label>
                      <select style={s.input} value={cajaPagoId} onChange={e => setCajaPagoId(e.target.value)}>
                        <option value="">Automática (según forma de pago)</option>
                        {cajasPago.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                      </select>
                    </div>
                  )}

                  {/* ✅ ABONOS: en formas distintas a efectivo, un monto menor
                      al saldo queda registrado como ABONO parcial */}
                  <div style={s.campo}>
                    <label style={s.label}>Monto recibido <span style={{ color: '#9ca3af', fontWeight: 400 }}>
                      {formaPago === 'Efectivo' ? '(paga con — para calcular el vuelto)' : '(vacío = saldo completo · un valor menor queda como abono)'}
                    </span></label>
                    <input type="number" style={s.input} placeholder={String(Math.max(0, (orden.total || 0) - (orden.montoPagado || 0)))}
                      value={montoPago} onChange={e => setMontoPago(e.target.value)} />
                  </div>

                  {/* Calculadora de vuelto — solo efectivo */}
                  {formaPago === 'Efectivo' && (() => {
                    const total = orden.total || 0;
                    const BILLETES = [100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50];
                    const [pagaCon, setPagaCon] = [montoPago, setMontoPago];
                    const monto = parseFloat(pagaCon) || 0;
                    const vuelto = monto - total;
                    return (
                      <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: 14, marginBottom: 14 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#166534', marginBottom: 10 }}>💵 Calculadora de vuelto</div>
                        <div style={{ fontSize: 12, color: '#374151', marginBottom: 8 }}>¿Con cuánto paga el cliente?</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                          {BILLETES.filter(b => b >= total || b >= 1000).slice(0, 8).map(b => (
                            <button key={b} type="button"
                              onClick={() => setMontoPago(String(b))}
                              style={{
                                padding: '6px 12px', borderRadius: 8, border: '2px solid',
                                borderColor: parseFloat(pagaCon) === b ? '#16a34a' : '#d1fae5',
                                background: parseFloat(pagaCon) === b ? '#16a34a' : '#fff',
                                color: parseFloat(pagaCon) === b ? '#fff' : '#166534',
                                fontSize: 12, fontWeight: 700, cursor: 'pointer'
                              }}>
                              ${new Intl.NumberFormat('es-CO').format(b)}
                            </button>
                          ))}
                        </div>
                        {monto > 0 && monto >= total && (
                          <div style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', border: '1px solid #86efac' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#374151', marginBottom: 4 }}>
                              <span>Total orden:</span>
                              <span style={{ fontWeight: 700 }}>{new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(total)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#374151', marginBottom: 6 }}>
                              <span>Paga con:</span>
                              <span style={{ fontWeight: 700 }}>{new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(monto)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 800, color: '#16a34a', borderTop: '1px solid #bbf7d0', paddingTop: 6 }}>
                              <span>Vuelto:</span>
                              <span>{new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(vuelto)}</span>
                            </div>
                          </div>
                        )}
                        {monto > 0 && monto < total && (
                          <div style={{ background: '#fef2f2', borderRadius: 8, padding: '10px 14px', border: '1px solid #fca5a5', fontSize: 13, color: '#dc2626', fontWeight: 700 }}>
                            ⚠️ El monto ingresado es menor al total de la orden
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Aviso CxC */}
                  {(formaPago === 'A crédito (CxC)' || formaPago === 'A crédito') && (
                    <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: 10, fontSize: 12, color: '#92400e', marginBottom: 12 }}>
                      ⚠️ Esta orden quedará en Cuentas por Cobrar. No afecta caja.
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={registrarPago} disabled={registrandoPago || !formaPago} style={{ ...s.btnPrimario, opacity: !formaPago ? 0.5 : 1 }}>
                      {registrandoPago ? 'Registrando...' : '✅ Confirmar pago'}
                    </button>
                    <button onClick={() => { setMostrarPago(false); setFormaPago(''); setMontoPago(''); }} style={s.btnSecundario}>Cancelar</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── ESTADO ───────────────────────────────────────────────────── */}
          <div style={s.card}>
            <h3 style={s.cardTitulo}>🔄 Estado de la orden</h3>

            {/* Historial */}
            <div style={{ marginBottom: '20px' }}>
              {(orden.historialEstados || []).map((h, i) => {
                const estInfo = ESTADOS[h.estado] || { label: h.estado, color: '#666' };
                // FIX Bug 2: si la orden está pagada, mostrar "Entrega" en lugar
                // de "Entrega Cobranza" (porque ya no hay cobranza pendiente).
                let labelMostrar = estInfo.label;
                if (h.estado === 'entrega_cobranza' && orden.pagado === true) {
                  labelMostrar = 'Entrega';
                }
                return (
                  <div key={i} style={s.historialItem}>
                    <div style={{ ...s.historialDot, background: estInfo.color }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, color: estInfo.color, fontSize: '13px' }}>{labelMostrar}</div>
                      <div style={{ fontSize: '11px', color: '#9ca3af' }}>{h.usuarioNombre} — {formatFechaCorta(h.fecha)}</div>
                      {h.notas && <div style={{ fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>{h.notas}</div>}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Siguiente estado */}
           {mostrarSiguienteEstado && (
  <>
  {(orden.estado === 'facturado' || (orden.estado === 'despacho' && orden.requiereFactura)) && (
  <div style={{ marginBottom: '12px' }}>
    <label style={{ ...s.label, marginBottom: 6, display: 'block' }}>
      📄 N° Factura DIAN
      {orden.requiereFactura && <span style={{ marginLeft: 6, fontSize: 11, color: '#d97706', fontWeight: 700 }}>obligatorio</span>}
    </label>
    <input style={{ ...s.input }}
      placeholder="FE-0001..."
      value={numeroFactura}
      onChange={e => setNumeroFactura(e.target.value)}
    />
  </div>
)}
    <input style={{ ...s.input, marginBottom: '12px' }}
      placeholder="Notas del cambio (opcional)..."
      value={notasEstado}
      onChange={e => setNotasEstado(e.target.value)}
    />
               {(() => {
    // Bloqueo de factura: aplica cuando la orden requiere factura Y el
    // siguiente paso sale del estado 'facturado' o entra a él (cualquier
    // flujo, no solo despacho). Resuelve tu bug de domicilio IVA 19%.
    // ✅ FACTURA-ATASCO-001: espejo de `estadosQueExigenFactura` en
    // backend/routes/orders.js. Antes esta lista era más corta que la del
    // backend: el botón se veía habilitado y el servidor respondía con el
    // error rojo. Mantener las dos listas iguales.
    const ESTADOS_EXIGEN_FACTURA = ['facturado', 'completada', 'cxc', 'en_ruta_entrega',
      'entrega_cobranza', 'cuadre_dinero', 'listo_entregar'];
    const cruzaFactura = orden.requiereFactura && !numeroFactura && !orden.numeroFactura &&
      (ESTADOS_EXIGEN_FACTURA.includes(orden.estado) || ESTADOS_EXIGEN_FACTURA.includes(siguienteEstado));
    // Etiqueta especial: cuando el equipo de taller espera que el cliente venga
    // FIX Bug 2: si va a "entrega_cobranza" y ya está pagada, decir "Entrega" sin "Cobranza"
    let labelEstadoSiguiente = ESTADOS[siguienteEstado]?.label || siguienteEstado;
    if (siguienteEstado === 'entrega_cobranza' && orden.pagado === true) {
      labelEstadoSiguiente = 'Entrega';
    }
    const labelBoton = orden.estado === 'listo_entregar'
      ? '✓ Cliente recogió el equipo'
      : `→ Pasar a ${labelEstadoSiguiente}`;
    return (
      <>
        {cruzaFactura && (
          <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, color: '#dc2626', fontWeight: 600, marginBottom: 8 }}>
            ⚠️ Debes ingresar el N° de factura DIAN antes de continuar
          </div>
        )}
        {puedeAvanzarEstado ? (
          <button onClick={() => cambiarEstado(siguienteEstado)}
            disabled={cambiandoEstado || cruzaFactura}
            style={{ ...s.btnEstado, background: cruzaFactura ? '#9ca3af' : (ESTADOS[siguienteEstado]?.color || '#7c3aed') }}>
            {cambiandoEstado ? 'Actualizando...' : labelBoton}
          </button>
        ) : (
          <div style={{ padding: '10px 14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 12, color: '#6b7280', textAlign: 'center' }}>
            🔒 Solo el administrador puede avanzar el estado de la orden
          </div>
        )}
      </>
    );
  })()}
              </>
            )}

            {!mostrarSiguienteEstado && orden.estado !== 'anulada'
              && (orden.estado === 'cuadre_dinero' || orden.estado === 'completada') && (
              <div style={{ padding: '12px', background: '#f0fdf4', borderRadius: '8px', color: '#16a34a', fontWeight: 700, textAlign: 'center' }}>
                ✅ Orden completada
              </div>
            )}
            {!mostrarSiguienteEstado && orden.estado === 'cxc' && (
              <div style={{ padding: '12px', background: '#fffbeb', borderRadius: '8px', color: '#b45309', fontWeight: 700, textAlign: 'center' }}>
                💳 Orden en Cuenta por Cobrar
              </div>
            )}
          </div>

          {/* ── COLUMNA DERECHA: empieza con Info de la orden ── */}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL: ANULAR ORDEN  (Ola 1: motivo + PIN del admin/tesorería)
      ══════════════════════════════════════════════════════════════════════ */}
      {mostrarAnular && (
        <div style={sAnular.overlay} onClick={() => !anulando && setMostrarAnular(false)}>
          <div style={sAnular.modal} onClick={e => e.stopPropagation()}>
            <div style={sAnular.header}>
              <div style={sAnular.iconCircle}>🚫</div>
              <div>
                <h3 style={sAnular.titulo}>Anular orden {orden?.numeroOrden}</h3>
                <p style={sAnular.subtitulo}>Esta acción no se puede deshacer. Devuelve el stock y registra la anulación en auditoría.</p>
              </div>
            </div>

            {error && <div style={sAnular.alertError}>⚠ {error}</div>}

            <div style={sAnular.body}>
              <div style={sAnular.campo}>
                <label style={sAnular.label}>
                  Motivo de la anulación <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <textarea
                  style={sAnular.textarea}
                  rows={3}
                  placeholder="Ej: Cliente canceló el pedido por cambio de presupuesto"
                  value={motivoAnular}
                  onChange={e => setMotivoAnular(e.target.value)}
                  disabled={anulando}
                />
              </div>

              <div style={sAnular.campo}>
                <label style={sAnular.label}>
                  PIN de autorización <span style={{ color: '#dc2626' }}>*</span>
                  <small style={sAnular.hint}>Solo administradores con PIN configurado pueden anular.</small>
                </label>
                <div style={sAnular.pinWrap}>
                  <input
                    type={verPinAnular ? 'text' : 'password'}
                    style={sAnular.pinInput}
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="0000"
                    value={pinAnular}
                    onChange={e => setPinAnular(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    disabled={anulando}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setVerPinAnular(!verPinAnular)}
                    style={sAnular.eyeBtn}
                    disabled={anulando}
                  >
                    {verPinAnular ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
            </div>

            <div style={sAnular.footer}>
              <button
                onClick={() => setMostrarAnular(false)}
                style={sAnular.btnCancelar}
                disabled={anulando}
              >
                Cancelar
              </button>
              <button
                onClick={confirmarAnular}
                style={{
                  ...sAnular.btnAnular,
                  opacity: anulando || !motivoAnular.trim() || pinAnular.length !== 4 ? 0.5 : 1,
                  cursor: anulando || !motivoAnular.trim() || pinAnular.length !== 4 ? 'not-allowed' : 'pointer'
                }}
                disabled={anulando || !motivoAnular.trim() || pinAnular.length !== 4}
              >
                {anulando ? 'Anulando...' : '🚫 Confirmar anulación'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL: VALIDAR PAGO ELECTRÓNICO (Ola 2.5 — Admin/Tesorería)
          Aprueba o rechaza el comprobante de pago que subió el mensajero.
      ══════════════════════════════════════════════════════════════════════ */}
      {mostrarValidarPago && (
        <div style={sValidPago.overlay} onClick={() => !validandoPago && setMostrarValidarPago(false)}>
          <div style={sValidPago.modal} onClick={e => e.stopPropagation()}>
            <div style={{
              ...sValidPago.header,
              background: accionPago === 'aprobar'
                ? 'linear-gradient(135deg, #f0fdf4 0%, #fff 100%)'
                : 'linear-gradient(135deg, #fef2f2 0%, #fff 100%)'
            }}>
              <div style={{
                ...sValidPago.iconCircle,
                background: accionPago === 'aprobar' ? '#dcfce7' : '#fee2e2'
              }}>
                {accionPago === 'aprobar' ? '✅' : '❌'}
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={sValidPago.titulo}>
                  {accionPago === 'aprobar' ? 'Aprobar pago electrónico' : 'Rechazar pago electrónico'}
                </h3>
                <p style={sValidPago.subtitulo}>
                  Orden {orden?.numeroOrden} · {orden?.formaPago} · {formatCOP(orden?.total)}
                </p>
              </div>
            </div>

            {error && <div style={sValidPago.alertError}>⚠ {error}</div>}

            <div style={sValidPago.body}>
              {/* Información del pago */}
              <div style={sValidPago.infoBox}>
                <div style={sValidPago.infoRow}>
                  <span style={sValidPago.infoLabel}>Cliente</span>
                  <span style={sValidPago.infoValor}>{orden?.clienteNombre}</span>
                </div>
                <div style={sValidPago.infoRow}>
                  <span style={sValidPago.infoLabel}>Forma de pago</span>
                  <span style={sValidPago.infoValor}>{orden?.formaPago}</span>
                </div>
                <div style={sValidPago.infoRow}>
                  <span style={sValidPago.infoLabel}>Monto</span>
                  <span style={{ ...sValidPago.infoValor, fontWeight: 800, color: '#16a34a', fontSize: 16 }}>
                    {formatCOP(orden?.total || 0)}
                  </span>
                </div>
                {orden?.fotoTransferenciaUrl && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ ...sValidPago.infoLabel, marginBottom: 8 }}>📸 Comprobante</div>
                    <a href={orden.fotoTransferenciaUrl} target="_blank" rel="noreferrer">
                      <img
                        src={orden.fotoTransferenciaUrl}
                        alt="Comprobante de pago"
                        style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer' }}
                      />
                    </a>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                      Click en la imagen para verla en tamaño completo
                    </div>
                  </div>
                )}
                {!orden?.fotoTransferenciaUrl && (
                  <div style={{
                    marginTop: 8, padding: '10px 12px',
                    background: '#fffbeb', border: '1px solid #fcd34d',
                    borderRadius: 8, fontSize: 12, color: '#92400e'
                  }}>
                    ⚠ El mensajero no subió foto del comprobante. Verifica en el banco antes de aprobar.
                  </div>
                )}
              </div>

              {/* ✅ VALIDAR-CAJA-001: forma de pago exacta + caja destino */}
              {accionPago === 'aprobar' && (
                <>
                  <div style={sValidPago.campo}>
                    <label style={sValidPago.label}>Forma de pago confirmada</label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {formasPago.filter(f => !(f || '').toLowerCase().includes('efectivo') && f !== 'A crédito (CxC)' && f !== 'A crédito' && f !== 'CXC' && f !== 'Cuenta por Pagar').map(f => (
                        <button key={f} type="button" onClick={() => setFormaPagoConfirmada(f)} disabled={validandoPago} style={{
                          padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700,
                          border: formaPagoConfirmada === f ? '2px solid #0284c7' : '1px solid #e5e7eb',
                          background: formaPagoConfirmada === f ? '#eff6ff' : '#fff',
                          color: formaPagoConfirmada === f ? '#075985' : '#374151'
                        }}>{f}</button>
                      ))}
                    </div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                      El mensajero solo declaró "electrónico" — aquí confirmas por dónde entró realmente.
                    </div>
                  </div>
                  {cajasDisp.length > 0 && (
                    <div style={sValidPago.campo}>
                      <label style={sValidPago.label}>Caja destino del dinero <span style={{ color: '#dc2626' }}>*</span></label>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {cajasDisp.map(c => (
                          <button key={c.id} type="button" onClick={() => setCajaValidacionId(c.id)} disabled={validandoPago} style={{
                            padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700,
                            border: cajaValidacionId === c.id ? '2px solid #16a34a' : '1px solid #e5e7eb',
                            background: cajaValidacionId === c.id ? '#f0fdf4' : '#fff',
                            color: cajaValidacionId === c.id ? '#166534' : '#374151'
                          }}>{c.nombre}</button>
                        ))}
                      </div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                        Si no eliges, el sistema usa el mapeo automático de Mi Empresa.
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Motivo */}
              <div style={sValidPago.campo}>
                <label style={sValidPago.label}>
                  {accionPago === 'aprobar'
                    ? 'Notas (opcional)'
                    : <>Motivo del rechazo <span style={{ color: '#dc2626' }}>*</span></>
                  }
                </label>
                <textarea
                  style={sValidPago.textarea}
                  rows={2}
                  placeholder={accionPago === 'aprobar'
                    ? 'Ej: Confirmé el ingreso en el banco a las 3pm'
                    : 'Ej: La transferencia nunca llegó al banco / Comprobante alterado'
                  }
                  value={motivoValidacion}
                  onChange={e => setMotivoValidacion(e.target.value)}
                  disabled={validandoPago}
                />
              </div>

              {/* PIN */}
              <div style={sValidPago.campo}>
                <label style={sValidPago.label}>
                  PIN de autorización <span style={{ color: '#dc2626' }}>*</span>
                  <small style={sValidPago.hint}>Tu PIN de Admin o Tesorería</small>
                </label>
                <div style={sValidPago.pinWrap}>
                  <input
                    type={verPinValidacion ? 'text' : 'password'}
                    style={sValidPago.pinInput}
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="0000"
                    value={pinValidacion}
                    onChange={e => setPinValidacion(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    disabled={validandoPago}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setVerPinValidacion(!verPinValidacion)}
                    style={sValidPago.eyeBtn}
                    disabled={validandoPago}
                  >
                    {verPinValidacion ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              {/* Aviso explicativo */}
              <div style={{
                padding: '10px 12px',
                background: accionPago === 'aprobar' ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${accionPago === 'aprobar' ? '#86efac' : '#fca5a5'}`,
                borderRadius: 8, fontSize: 12,
                color: accionPago === 'aprobar' ? '#166534' : '#991b1b'
              }}>
                {accionPago === 'aprobar'
                  ? '✅ Al aprobar: el dinero se suma a la caja, la orden queda COMPLETADA y queda registrado en auditoría que tú validaste el ingreso.'
                  : '❌ Al rechazar: la orden pasa a CxC (el cliente queda debiendo). El motivo se registra en auditoría.'
                }
              </div>
            </div>

            <div style={sValidPago.footer}>
              <button
                onClick={() => setMostrarValidarPago(false)}
                style={sValidPago.btnCancelar}
                disabled={validandoPago}
              >
                Cancelar
              </button>
              <button
                onClick={confirmarValidarPago}
                style={{
                  ...(accionPago === 'aprobar' ? sValidPago.btnAprobar : sValidPago.btnRechazar),
                  opacity: validandoPago || pinValidacion.length !== 4 ||
                          (accionPago === 'rechazar' && motivoValidacion.trim().length < 5) ? 0.5 : 1,
                  cursor: validandoPago ? 'not-allowed' : 'pointer'
                }}
                disabled={validandoPago || pinValidacion.length !== 4 ||
                         (accionPago === 'rechazar' && motivoValidacion.trim().length < 5)}
              >
                {validandoPago
                  ? 'Procesando...'
                  : accionPago === 'aprobar' ? '✅ Aprobar pago' : '❌ Rechazar pago'
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

// ─── ESTILOS DEL MODAL DE ANULAR ──────────────────────────────────────────────
const sAnular = {
  overlay:     { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '20px' },
  modal:       { background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '480px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.35)', fontFamily: "'Segoe UI', sans-serif" },
  header:      { display: 'flex', gap: '14px', alignItems: 'flex-start', padding: '24px 24px 12px', borderBottom: '1px solid #fef2f2', background: 'linear-gradient(135deg, #fef2f2 0%, #fff 100%)' },
  iconCircle:  { width: '44px', height: '44px', borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 },
  titulo:      { margin: 0, fontSize: '17px', fontWeight: 700, color: '#111' },
  subtitulo:   { margin: '4px 0 0', fontSize: '13px', color: '#6b7280', lineHeight: 1.4 },
  alertError:  { margin: '12px 24px 0', padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: '8px', fontSize: '13px', fontWeight: 500 },
  body:        { padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '18px' },
  campo:       { display: 'flex', flexDirection: 'column', gap: '6px' },
  label:       { fontSize: '13px', fontWeight: 700, color: '#374151', display: 'flex', flexDirection: 'column', gap: '2px' },
  hint:        { fontWeight: 400, fontSize: '11px', color: '#9ca3af', marginTop: '2px' },
  textarea:    { padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', outline: 'none', fontFamily: "'Segoe UI', sans-serif", resize: 'vertical', minHeight: '70px', boxSizing: 'border-box' },
  pinWrap:     { position: 'relative', width: '160px' },
  pinInput:    { padding: '12px 44px 12px 14px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '22px', outline: 'none', textAlign: 'center', letterSpacing: '10px', fontFamily: 'monospace', width: '100%', boxSizing: 'border-box' },
  eyeBtn:      { position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '4px' },
  footer:      { padding: '16px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: '10px', background: '#fafafa' },
  btnCancelar: { padding: '10px 20px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' },
  btnAnular:   { padding: '10px 22px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '14px' },
};

// ─── ESTILOS DEL MODAL VALIDAR PAGO (Ola 2.5) ──────────────────────────────
const sValidPago = {
  overlay:     { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '20px' },
  modal:       { background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.35)', fontFamily: "'Segoe UI', sans-serif" },
  header:      { display: 'flex', gap: '14px', alignItems: 'flex-start', padding: '24px 24px 14px', borderBottom: '1px solid #f3f4f6' },
  iconCircle:  { width: '44px', height: '44px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 },
  titulo:      { margin: 0, fontSize: '17px', fontWeight: 700, color: '#111' },
  subtitulo:   { margin: '4px 0 0', fontSize: '13px', color: '#6b7280' },
  alertError:  { margin: '12px 24px 0', padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: '8px', fontSize: '13px', fontWeight: 500 },
  body:        { padding: '18px 24px', display: 'flex', flexDirection: 'column', gap: '16px' },
  infoBox:     { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px 16px' },
  infoRow:     { display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' },
  infoLabel:   { color: '#6b7280', fontWeight: 600 },
  infoValor:   { color: '#111', fontWeight: 600 },
  campo:       { display: 'flex', flexDirection: 'column', gap: '6px' },
  label:       { fontSize: '13px', fontWeight: 700, color: '#374151', display: 'flex', flexDirection: 'column', gap: '2px' },
  hint:        { fontWeight: 400, fontSize: '11px', color: '#9ca3af', marginTop: '2px' },
  textarea:    { padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', outline: 'none', fontFamily: "'Segoe UI', sans-serif", resize: 'vertical', minHeight: '60px', boxSizing: 'border-box' },
  pinWrap:     { position: 'relative', width: '160px' },
  pinInput:    { padding: '12px 44px 12px 14px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '22px', outline: 'none', textAlign: 'center', letterSpacing: '10px', fontFamily: 'monospace', width: '100%', boxSizing: 'border-box' },
  eyeBtn:      { position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '4px' },
  footer:      { padding: '16px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: '10px', background: '#fafafa' },
  btnCancelar: { padding: '10px 20px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' },
  btnAprobar:  { padding: '10px 22px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '14px' },
  btnRechazar: { padding: '10px 22px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '14px' },
};


// ─── GENERADOR HTML CERTIFICADO ──────────────────────────────────────────────
const generarHTMLCertificadoDinamico = (orden, empresa, configCat = null) => {
  const fechaServicio = new Date(orden.fechaProgramada || new Date()).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' });
  const proximoAño = new Date(orden.fechaProgramada || new Date());
  proximoAño.setFullYear(proximoAño.getFullYear() + 1);
  const fechaProxima = proximoAño.toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' });

  const nombreDoc = configCat?.nombreDocumento || 'Certificado de Mantenimiento de Extintores';
  const norma = configCat?.norma || 'NTC 2885';
  const texto = configCat?.texto || 'Por medio del presente documento certificamos que se realizaron los servicios a los equipos relacionados a continuación.';
  const contenido = configCat?.contenido || '';

  // Filtrar items según categoría si hay config
  const items = configCat
    ? (orden.items || []).filter(item => {
        const itemCat = (item.categoria || '').toLowerCase();
        const catNorm = (configCat.categoriaProducto || '').toLowerCase();
        return itemCat.includes(catNorm) || catNorm.includes(itemCat);
      })
    : (orden.items || []).filter(i => {
        const cat = (i.categoria || '').toLowerCase();
        return cat.includes('recarga') || cat.includes('mantenimiento');
      });

  const filas = items.map((item, i) => `
    <tr>
      <td style="text-align:center;padding:6px 8px;border:1px solid #ddd">${i + 1}</td>
      <td style="padding:6px 8px;border:1px solid #ddd">${item.nombre}</td>
      <td style="text-align:center;padding:6px 8px;border:1px solid #ddd">${item.cantidad}</td>
      <td style="text-align:center;padding:6px 8px;border:1px solid #ddd">—</td>
      <td style="text-align:center;padding:6px 8px;border:1px solid #ddd">${fechaServicio}</td>
      <td style="text-align:center;padding:6px 8px;border:1px solid #ddd;color:#dc2626;font-weight:bold">${fechaProxima}</td>
    </tr>`).join('');

  return `<!DOCTYPE html><html><head><meta charset="UTF-8">
  <title>${nombreDoc} — ${orden.numeroOrden}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 11px; padding: 15mm; color: #333; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #4c1d95; padding-bottom: 12px; margin-bottom: 16px; }
    .empresa-info h1 { font-size: 16px; color: #4c1d95; text-transform: uppercase; }
    .empresa-info p { font-size: 10px; color: #666; margin-top: 2px; }
    .cert-num { text-align: right; font-size: 10px; color: #666; }
    .cert-titulo { text-align: center; margin: 16px 0; }
    .cert-titulo h2 { font-size: 18px; color: #1e1b4b; text-transform: uppercase; }
    .cert-titulo p { font-size: 11px; color: #6b7280; margin-top: 4px; }
    .saludo { margin: 16px 0; line-height: 1.6; }
    table { width: 100%; border-collapse: collapse; margin: 16px 0; font-size: 10px; }
    th { background: #ede9fe; color: #4c1d95; padding: 6px 8px; font-size: 10px; border: 1px solid #ddd; }
    .normativa { margin: 16px 0; background: #f9fafb; border-left: 4px solid #4c1d95; padding: 12px; font-size: 10px; line-height: 1.6; }
    .firma { margin-top: 32px; display: flex; justify-content: space-between; }
    .firma-box { text-align: center; width: 45%; }
    .firma-linea { border-top: 1px solid #333; margin-bottom: 4px; padding-top: 6px; }
    .footer { text-align: center; margin-top: 24px; padding-top: 12px; border-top: 2px dashed #ccc; font-size: 9px; color: #9ca3af; }
    @media print { body { padding: 10mm; } }
  </style></head><body>
  <div class="header">
    <div class="empresa-info">
      ${empresa?.logo ? `<img src="${empresa.logo}" style="height:90px;max-width:200px;object-fit:contain;margin:0 auto 8px;display:block"/><br/>` : ''}
      <h1>${empresa?.name || ''}</h1>
      <p>NIT: ${empresa?.nit || ''} | Tel: ${empresa?.cellphone || empresa?.phone || ''}</p>
      <p>${empresa?.address || ''}</p>
      <p>${empresa?.email || ''}</p>
    </div>
    <div class="cert-num">
      <p><strong>CERT-${orden.numeroOrden}</strong></p>
      <p>Fecha: ${fechaServicio}</p>
      <p>OS: ${orden.numeroOrden}</p>
    </div>
  </div>
  <div class="cert-titulo">
    <h2>${nombreDoc}</h2>
    ${norma ? `<p>Según ${norma}</p>` : ''}
  </div>
  <div class="saludo">
    <p>Estimados señores:</p>
    <p><strong>${orden.clienteNombre}</strong>${orden.clienteNit ? ` — NIT: ${orden.clienteNit}` : ''}</p>
    <br/>
    <p>${texto}</p>
  </div>
  ${filas ? `
  <table>
    <thead><tr><th>#</th><th>Descripción</th><th>Unidades</th><th>N° Serie</th><th>Fecha servicio</th><th>Próximo servicio</th></tr></thead>
    <tbody>${filas}</tbody>
  </table>` : ''}
  ${contenido ? `<div class="normativa"><strong>Características / Servicios realizados:</strong><br/>${contenido.replace(/\n/g, '<br/>')}</div>` : ''}
  <div class="firma">
    <div class="firma-box"><div style="height:40px"></div><div class="firma-linea"><strong>${empresa?.name || ''}</strong><br/><span style="font-size:9px;color:#666">Empresa prestadora</span></div></div>
    <div class="firma-box"><div style="height:40px"></div><div class="firma-linea"><strong>Técnico responsable</strong><br/><span style="font-size:9px;color:#666">Firma autorizada</span></div></div>
  </div>
  <div class="footer">Documento generado por <strong>Control360</strong></div>
  </body></html>`;
};

const generarHTMLCertificado = (orden, empresa) => {
  const fechaServicio = formatFecha(orden.fechaProgramada || new Date().toISOString());
  const proximoAño = new Date(orden.fechaProgramada || new Date());
  proximoAño.setFullYear(proximoAño.getFullYear() + 1);
  const fechaProxima = formatFecha(proximoAño.toISOString());

  const itemsRecarga = (orden.items || []).filter(i => {
    const cat = (i.categoria || '').toLowerCase();
    return cat.includes('recarga') || cat.includes('mantenimiento');
  });
  const itemsPH = (orden.items || []).filter(i => {
    const cat = (i.categoria || '').toLowerCase();
    return cat.includes('prueba') || cat.includes('hidrost') || cat.includes(' ph');
  });

  const filaRecarga = (item, i) => `
    <tr>
      <td style="text-align:center">${i + 1}</td>
      <td>${item.nombre}</td>
      <td style="text-align:center">${item.cantidad}</td>
      <td style="text-align:center">—</td>
      <td style="text-align:center">${fechaServicio}</td>
      <td style="text-align:center;color:#dc2626;font-weight:bold">${fechaProxima}</td>
    </tr>`;

  const filaPH = (item, i) => `
    <tr>
      <td style="text-align:center">${i + 1}</td>
      <td>${item.nombre}</td>
      <td style="text-align:center">${item.cantidad}</td>
      <td style="text-align:center">—</td>
      <td style="text-align:center">${fechaServicio}</td>
      <td style="text-align:center;color:#dc2626;font-weight:bold">${formatFecha(new Date(new Date(orden.fechaProgramada || new Date()).setFullYear(new Date(orden.fechaProgramada || new Date()).getFullYear() + 5)).toISOString())}</td>
    </tr>`;

  return `<!DOCTYPE html><html><head><meta charset="UTF-8">
  <title>Certificado ${orden.numeroOrden}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 11px; padding: 15mm; color: #333; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #4c1d95; padding-bottom: 12px; margin-bottom: 16px; }
    .empresa-info h1 { font-size: 16px; color: #4c1d95; text-transform: uppercase; }
    .empresa-info p { font-size: 10px; color: #666; margin-top: 2px; }
    .cert-num { text-align: right; font-size: 10px; color: #666; }
    .cert-titulo { text-align: center; margin: 16px 0; }
    .cert-titulo h2 { font-size: 18px; color: #1e1b4b; letter-spacing: 1px; text-transform: uppercase; }
    .cert-titulo p { font-size: 11px; color: #6b7280; margin-top: 4px; }
    .saludo { margin: 16px 0; line-height: 1.6; }
    .seccion { margin: 16px 0; }
    .seccion h3 { background: #1e1b4b; color: white; padding: 6px 12px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 0; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #ede9fe; color: #4c1d95; padding: 6px 8px; font-size: 10px; text-align: left; font-weight: bold; border: 1px solid #ddd; }
    td { padding: 6px 8px; border: 1px solid #ddd; font-size: 10px; }
    tr:nth-child(even) td { background: #f9f9f9; }
    .normativa { margin: 16px 0; background: #f9fafb; border-left: 4px solid #4c1d95; padding: 12px; font-size: 10px; line-height: 1.6; }
    .firma { margin-top: 32px; display: flex; justify-content: space-between; }
    .firma-box { text-align: center; width: 45%; }
    .firma-linea { border-top: 1px solid #333; margin-bottom: 4px; padding-top: 6px; }
    .footer { text-align: center; margin-top: 24px; padding-top: 12px; border-top: 2px dashed #ccc; font-size: 9px; color: #9ca3af; }
    @media print { body { padding: 10mm; } }
  </style></head><body>
  <div class="header">
    <div class="empresa-info">
      ${empresa?.logo ? `<img src="${empresa.logo}" style="height:90px;max-width:200px;object-fit:contain;margin:0 auto 8px;display:block" /><br/>` : ''}
      <h1>${empresa?.name || 'EXTINTORES DEL SUR'}</h1>
      <p>NIT: ${empresa?.nit || ''} | Tel: ${empresa?.cellphone || empresa?.phone || ''}</p>
      <p>${empresa?.address || ''}</p>
      <p>${empresa?.email || ''}</p>
    </div>
    <div class="cert-num">
      <p><strong>CERT-${orden.numeroOrden}</strong></p>
      <p>Fecha: ${fechaServicio}</p>
      <p>OS: ${orden.numeroOrden}</p>
    </div>
  </div>
  <div class="cert-titulo">
    <h2>Certificado de Mantenimiento de Extintores</h2>
    <p>Según Norma Técnica Colombiana NTC 2885 — Extintores Portátiles Contra Incendio</p>
  </div>
  <div class="saludo">
    <p>Estimados señores:</p>
    <p><strong>${orden.clienteNombre}</strong>${orden.clienteNit ? ` — NIT: ${orden.clienteNit}` : ''}</p>
    ${orden.sucursalDireccion ? `<p>${orden.sucursalDireccion}</p>` : ''}
    <br/>
    <p>Por medio del presente documento certificamos que se realizaron los servicios de mantenimiento a los extintores portátiles contra incendio relacionados a continuación, en cumplimiento de la Norma Técnica Colombiana <strong>NTC 2885</strong>.</p>
  </div>
  ${itemsRecarga.length > 0 ? `
  <div class="seccion">
    <h3>🔧 Recargas y Mantenimiento Realizados</h3>
    <table>
      <thead><tr><th>#</th><th>Descripción</th><th>Unidades</th><th>N° Serie</th><th>Fecha servicio</th><th>Próxima recarga</th></tr></thead>
      <tbody>${itemsRecarga.map((item, i) => filaRecarga(item, i)).join('')}</tbody>
    </table>
  </div>` : ''}
  ${itemsPH.length > 0 ? `
  <div class="seccion">
    <h3>🧪 Pruebas Hidrostáticas</h3>
    <table>
      <thead><tr><th>#</th><th>Descripción</th><th>Unidades</th><th>N° Serie</th><th>Fecha</th><th>Próxima PH (5 años)</th></tr></thead>
      <tbody>${itemsPH.map((item, i) => filaPH(item, i)).join('')}</tbody>
    </table>
  </div>` : ''}
  <div class="normativa">
    <strong>Servicios realizados incluyen:</strong><br/>
    • Inspección visual interna y externa • Limpieza de válvulas y mecanismos<br/>
    • Cambio de empaques y sellos • Recarga del agente extintor e impulsor<br/>
    • Prueba de hermeticidad • Rotulado según NTC 2885
  </div>
  <div class="firma">
    <div class="firma-box"><div style="height:40px"></div><div class="firma-linea"><strong>${empresa?.name || ''}</strong><br/><span style="font-size:9px;color:#666">Empresa prestadora</span></div></div>
    <div class="firma-box"><div style="height:40px"></div><div class="firma-linea"><strong>Técnico responsable</strong><br/><span style="font-size:9px;color:#666">Mantenimiento de extintores</span></div></div>
  </div>
  <div class="footer">Documento generado por <strong>Control360</strong> | 📞 3148361622</div>
  </body></html>`;
};

// ─── ESTILOS ──────────────────────────────────────────────────────────────────
const s = {
  wrapper:      { padding: '32px', maxWidth: '1400px', margin: '0 auto', fontFamily: "'Segoe UI', sans-serif" },
  pageHeader:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  pageTitle:    { margin: 0, fontSize: '24px', fontWeight: 800, color: '#111' },
  pageSubtitle: { margin: '4px 0 0', color: '#6b7280', fontSize: '14px' },
  btnBack:      { background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer', fontSize: '14px', fontWeight: 600, padding: 0 },
  estadoBadge:  { padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 700 },
  urgBadge:     { padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, background: '#fef2f2', color: '#dc2626' },
  acciones:     { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  btnWa:        { padding: '10px 16px', background: '#25d366', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '13px' },
  btnImprimir:  { padding: '10px 16px', background: '#374151', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '13px' },
  btnCert:      { padding: '10px 16px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '13px' },
  btnPrimario:  { padding: '10px 20px', background: 'linear-gradient(135deg,#667eea,#764ba2)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '14px' },
  btnSecundario:{ padding: '10px 16px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 },
  btnEstado:    { width: '100%', padding: '12px', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '14px' },
  alertError:   { background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px' },
  alertExito:   { background: '#f0fdf4', border: '1px solid #86efac', color: '#16a34a', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px' },
  grid2:        { display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '20px' },
  card:         { background: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '20px', marginBottom: '16px' },
  cardTitulo:   { margin: '0 0 16px', fontSize: '16px', fontWeight: 700, color: '#111' },
  infoGrid:     { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  infoItem:     { display: 'flex', flexDirection: 'column', gap: '2px' },
  infoLabel:    { fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' },
  totalRow:     { display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '4px 0' },
  historialItem:{ display: 'flex', gap: '12px', paddingBottom: '12px', marginBottom: '12px', borderBottom: '1px solid #f3f4f6' },
  historialDot: { width: '10px', height: '10px', borderRadius: '50%', marginTop: '4px', flexShrink: 0 },
  input:        { padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' },
  campo:        { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '14px' },
  label:        { fontSize: '13px', fontWeight: 700, color: '#374151' },
};

export default DetalleOrden;

